import { Box, Button, Flex, Grid, Heading, Text } from '@chakra-ui/react';
import { BodyOverride } from '../shared/BodyOverride';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DataTableContainer } from '../shared/dataTable/DataTableContainer';
import { useTranslation } from 'react-i18next';

import { useActiveSession } from '@texas/hooks/useSession';
import { SelectedFilterValues, useFilters } from './useFilters';
import {
  SearchQuery,
  TransportRequest,
  transportRequestsApi,
} from '@texas/api/endpoints/transportRequestApi';
import { ShipmentState } from '@texas/api/endpoints/shipmentsApi';
import { transportRequestColumns } from './columns';
import { ShipmentSamplesModal } from './ShipmentSamplesModal';
import { UpdateTransportRequestModal } from '../shared/transportRequests/UpdateTransportRequest';
import { sampleEvents } from '../shared/samples/events';
import { useSignalSubscriptionEvents } from '../shared/hooks/useSignalSubscriptionEvents';
import { transportRequestEvents } from '../shared/transportRequests/events';
import { EntityDirectoryPage } from '../shared/entityDirectory/EntityDirectoryPage';
import { LoadingCount } from '../shared/LoadingCount';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { defaultIconSize, Icons } from '../shared/Icons';
import {
  excelExportFileName,
  exportPrefixes,
  saveBlobAsFile,
} from '@texas/utils/helpers/filesHelper';

const pageSize = 24;
const overviewPageSize = 1000;

export function Shipments() {
  const { currentUser } = useActiveSession();
  const { t } = useTranslation();

  const { filters, components, setIncludeDispatched } = useFilters({
    defaultValues: {
      branchId: currentUser.branchId,
      articleNumber: '',
      shipmentType: null,
      trackingNumber: '',
      customerId: null,
      includeDispatched: false,
      paOrItemCode: '',
    },
  });

  return (
    <BodyOverride>
      <EntityDirectoryPage
        description={t('shipment.overviewDescription')}
        title={t('shipment.shipments')}
      >
        <TransportRequests
          filterControls={components}
          filters={filters}
          setIncludeDispatched={setIncludeDispatched}
        />
      </EntityDirectoryPage>
    </BodyOverride>
  );
}

function TransportRequests({
  filterControls,
  filters,
  setIncludeDispatched,
}: {
  filterControls: React.ReactNode;
  filters: SelectedFilterValues;
  setIncludeDispatched: (includeDispatched: boolean) => void;
}) {
  const { t } = useTranslation();
  const [trId, setTrId] = useState<number>(0);
  const [editTrId, setEditTrId] = useState<number>(0);

  const { data, refetch, loading, error } = useApiResource(
    transportRequestsApi.filter,
  );
  const {
    data: overviewData,
    refetch: refetchOverview,
    loading: overviewLoading,
  } = useApiResource(transportRequestsApi.filter);
  const { request: exportProductApprovals, loading: exportLoading } =
    useApiRequest(transportRequestsApi.exportProductApprovals);

  const [searchQuery, setSearchQuery] = useState<SearchQuery>({
    sortBy: 'id',
    sortDesc: false,
    page: 1,
    pageSize,
    branchId: filters.branchId,
    shipmentType: filters.shipmentType,
    trackingNumber: filters.trackingNumber,
    customerId: filters.customerId,
    includeDispatched: filters.includeDispatched,
    paOrItemCode: filters.paOrItemCode,
  });

  const overviewQuery = useMemo<SearchQuery>(
    () => ({
      sortBy: 'id',
      sortDesc: false,
      page: 1,
      pageSize: overviewPageSize,
      branchId: filters.branchId,
      shipmentType: filters.shipmentType,
      trackingNumber: filters.trackingNumber,
      customerId: filters.customerId,
      includeDispatched: true,
      paOrItemCode: filters.paOrItemCode,
    }),
    [
      filters.branchId,
      filters.customerId,
      filters.paOrItemCode,
      filters.shipmentType,
      filters.trackingNumber,
    ],
  );

  const triggerRefetch = useCallback(() => {
    refetch(searchQuery);
    refetchOverview(overviewQuery);
  }, [overviewQuery, refetch, refetchOverview, searchQuery]);

  useEffect(() => {
    refetch(searchQuery);
  }, [refetch, searchQuery]);

  useEffect(() => {
    refetchOverview(overviewQuery);
  }, [overviewQuery, refetchOverview]);

  useSignalSubscriptionEvents({
    triggerOnEvent: triggerRefetch,
    signalEvents: [
      sampleEvents.onSampleRemovedFromTransportRequest,
      transportRequestEvents.onTransportUpdated,
    ],
  });

  useEffect(() => {
    setSearchQuery((currentQuery) => {
      if (
        currentQuery.page === 1 &&
        currentQuery.branchId === filters.branchId &&
        currentQuery.shipmentType === filters.shipmentType &&
        currentQuery.trackingNumber === filters.trackingNumber &&
        currentQuery.customerId === filters.customerId &&
        currentQuery.includeDispatched === filters.includeDispatched &&
        currentQuery.paOrItemCode === filters.paOrItemCode
      ) {
        return currentQuery;
      }

      return {
        ...currentQuery,
        page: 1,
        branchId: filters.branchId,
        shipmentType: filters.shipmentType,
        trackingNumber: filters.trackingNumber,
        customerId: filters.customerId,
        includeDispatched: filters.includeDispatched,
        paOrItemCode: filters.paOrItemCode,
      };
    });
  }, [filters]);

  const columns = useMemo(() => {
    return transportRequestColumns({
      t,
      onChange: triggerRefetch,
      onSamplesClick: setTrId,
      onEditTrClick: setEditTrId,
    });
  }, [t, triggerRefetch]);

  const overviewItems = overviewData?.items ?? [];
  const pendingCount = countPending(overviewItems);
  const dispatchedCount = overviewItems.length - pendingCount;
  const sampleCount = overviewItems
    .filter(isUndispatched)
    .reduce((count, request) => count + request.samplesInTransport, 0);
  const totalItems = data?.totalItems ?? 0;
  const firstVisibleItem = totalItems
    ? (searchQuery.page - 1) * pageSize + 1
    : 0;
  const lastVisibleItem = Math.min(searchQuery.page * pageSize, totalItems);

  const exportProductApprovalExcel = async () => {
    const response = await exportProductApprovals(searchQuery);

    if (response.hasError) return;

    saveBlobAsFile(
      response.data,
      excelExportFileName(exportPrefixes.productApprovals),
    );
  };

  return (
    <Flex flexDir="column" gap={5.5}>
      <ShipmentSummary
        isLoading={overviewLoading}
        total={overviewData?.totalItems ?? 0}
        pending={pendingCount}
        dispatched={dispatchedCount}
        samples={sampleCount}
      />

      <Box
        background="entityDirectory.surface"
        border="1px solid"
        borderColor="entityDirectory.border"
        borderRadius="3px"
        overflow="hidden"
      >
        <Flex
          align="stretch"
          borderBottom="1px solid"
          borderColor="entityDirectory.border"
          height="45px"
          px="22px"
        >
          <LifecycleButton
            active={!filters.includeDispatched}
            label={t('shipment.pendingRequests')}
            count={pendingCount}
            isLoading={overviewLoading}
            onClick={() => setIncludeDispatched(false)}
          />
          <LifecycleButton
            active={filters.includeDispatched}
            label={t('shipment.allRequests')}
            count={overviewData?.totalItems ?? 0}
            isLoading={overviewLoading}
            onClick={() => setIncludeDispatched(true)}
          />
        </Flex>
        <Flex minH="94px" align="center" px="22px" py={5}>
          {filterControls}
        </Flex>
      </Box>

      <Box
        background="entityDirectory.surface"
        border="1px solid"
        borderColor="entityDirectory.border"
        borderRadius="3px"
        overflow="hidden"
      >
        <Flex align="center" height="50px" justify="space-between" px={4.5}>
          <Flex align="baseline" gap={2}>
            <Heading fontFamily="barlow" fontSize="18px">
              {t('shipment.transportRequests')}
            </Heading>
            <Text
              color="entityDirectory.mutedText"
              fontFamily="instrumentSans"
              fontSize="12px"
            >
              {loading ? (
                <LoadingCount
                  isLoading={true}
                  value={totalItems}
                  minWidth="8ch"
                />
              ) : (
                t('shipment.requestCount', { count: totalItems })
              )}
            </Text>
          </Flex>
          <Button
            isDisabled={totalItems === 0}
            isLoading={exportLoading}
            leftIcon={<Icons.FileExcelBox boxSize={defaultIconSize} />}
            size="sm"
            variant="entityDirectorySecondary"
            onClick={exportProductApprovalExcel}
          >
            {t('general.exportExcel')}
          </Button>
        </Flex>
        <DataTableContainer
          w="100%"
          minH={0}
          display="flex"
          flexDir="column"
          error={error}
          css={{ ' th, td': { whiteSpace: 'normal' } }}
          tableId="shipments"
          datatable={{
            data: data?.items ?? [],
            columns,
            isLoading: loading,
            loadingOverlayDelay: 0,
            variant: 'shipment-overview',
            sorted: {
              onSortedChange: ({ key, desc }) => {
                setSearchQuery((currentQuery) => ({
                  ...currentQuery,
                  page: 1,
                  sortBy: key,
                  sortDesc: desc,
                }));
              },
              key: searchQuery.sortBy,
              desc: searchQuery.sortDesc,
            },
          }}
          footer={
            <Text
              color="entityDirectory.mutedText"
              fontFamily="instrumentSans"
              fontSize="12px"
            >
              {t('shipment.showingRequests', {
                first: firstVisibleItem,
                last: lastVisibleItem,
                total: totalItems,
              })}
            </Text>
          }
          footerContainerProps={{
            alignItems: 'center',
            borderTop: '1px solid',
            borderColor: 'entityDirectory.border',
            height: '44px',
            marginTop: 0,
            px: 4.5,
          }}
          pagination={{
            totalItems,
            pageSize,
            currentPage: searchQuery.page,
            onPageChange: (page) =>
              setSearchQuery((currentQuery) => ({
                ...currentQuery,
                page,
              })),
            variant: 'entityDirectoryPagination',
          }}
        />
      </Box>

      {trId !== 0 && (
        <ShipmentSamplesModal
          transportRequestId={trId}
          onClose={() => setTrId(0)}
        />
      )}
      {editTrId !== 0 && (
        <UpdateTransportRequestModal
          isOpen={editTrId !== 0}
          onClose={() => setEditTrId(0)}
          trId={editTrId}
        />
      )}
    </Flex>
  );
}

function ShipmentSummary({
  isLoading,
  total,
  pending,
  samples,
  dispatched,
}: {
  isLoading: boolean;
  total: number;
  pending: number;
  samples: number;
  dispatched: number;
}) {
  const { t } = useTranslation();
  const values = [
    { label: t('shipment.openRequests'), value: total },
    { label: t('shipment.awaitingShipment'), value: pending },
    {
      label: t('shipment.samplesInUndispatchedRequests'),
      value: samples,
    },
    { label: t('shipment.dispatched'), value: dispatched },
  ];

  return (
    <Grid
      background="entityDirectory.surface"
      border="1px solid"
      borderColor="entityDirectory.border"
      borderRadius="3px"
      gridTemplateColumns={{ base: '1fr 1fr', md: 'repeat(4, 1fr)' }}
      overflow="hidden"
    >
      {values.map((item, index) => (
        <Flex
          borderLeft={index % 4 === 0 ? 0 : '1px solid'}
          borderTop={{ base: index > 1 ? '1px solid' : 0, md: 0 }}
          borderColor="entityDirectory.border"
          flexDir="column"
          justify="center"
          minH="92px"
          px="22px"
          py={4}
          key={item.label}
        >
          <Text
            color="entityDirectory.mutedText"
            fontFamily="instrumentSans"
            fontSize="11px"
            fontWeight="bold"
            letterSpacing="0.08em"
            textTransform="uppercase"
          >
            {item.label}
          </Text>
          <Text
            fontFamily="barlow"
            fontSize="25px"
            fontWeight="bold"
            lineHeight="30px"
          >
            <LoadingCount isLoading={isLoading} value={item.value} />
          </Text>
        </Flex>
      ))}
    </Grid>
  );
}

function LifecycleButton({
  active,
  count,
  isLoading,
  label,
  onClick,
}: {
  active: boolean;
  count: number;
  isLoading: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <Button
      background="transparent"
      borderBottom="3px solid"
      borderBottomColor={
        active ? 'entityDirectory.primaryBackground' : 'transparent'
      }
      borderRadius={0}
      color={active ? 'entityDirectory.text' : 'entityDirectory.mutedText'}
      fontFamily="instrumentSans"
      fontSize="12px"
      fontWeight={active ? 'bold' : 'medium'}
      height="45px"
      px={3.5}
      _hover={{ background: 'entityDirectory.raisedSurface' }}
      onClick={onClick}
    >
      {label} · <LoadingCount isLoading={isLoading} value={count} />
    </Button>
  );
}

function countPending(requests: TransportRequest[]) {
  return requests.filter(isUndispatched).length;
}

function isUndispatched(request: TransportRequest) {
  return (
    request.shipment === null ||
    request.shipment.state === ShipmentState.AwaitingDispatch
  );
}
